<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth0 } from '@auth0/auth0-vue'
import { useUserStore } from '../stores/user'
import { trackEvent } from '../services/analytics'

const router = useRouter()
const { user: auth0User, isAuthenticated, isLoading: auth0Loading, getAccessTokenSilently } = useAuth0()
const userStore = useUserStore()

const status = ref('Completing login...')
const error = ref<string | null>(null)

function retry() {
    window.location.reload()
}

onMounted(async () => {
    // Check if Auth0 returned an error (e.g. Unauthorized, access_denied)
    const urlParams = new URLSearchParams(window.location.search)
    const authError = urlParams.get('error')
    const authErrorDesc = urlParams.get('error_description')
    if (authError) {
        console.error('[AuthCallback] Auth0 error:', authError, authErrorDesc)
        error.value = authErrorDesc || `Authentication failed: ${authError}`
        return
    }

    // Wait for Auth0 to finish loading (social login redirect return)
    let maxWait = 100 // 10 seconds max
    while (auth0Loading.value && maxWait > 0) {
        await new Promise(resolve => setTimeout(resolve, 100))
        maxWait--
    }

    // After loading, give the SDK a moment to set isAuthenticated
    if (!isAuthenticated.value) {
        await new Promise(resolve => setTimeout(resolve, 500))
    }

    console.log('[AuthCallback] Auth0 state:', {
        isAuthenticated: isAuthenticated.value,
        hasUser: !!auth0User.value,
        email: auth0User.value?.email,
        loading: auth0Loading.value
    })

    if (!isAuthenticated.value || !auth0User.value) {
        console.warn('[AuthCallback] Not authenticated after Auth0 load, redirecting to login')
        router.push('/login')
        return
    }

    try {
        status.value = 'Setting up your account...'
        const token = await getAccessTokenSilently()
        const email = auth0User.value.email!
        const name = auth0User.value.name || null

        console.log('[AuthCallback] Calling initUser for:', email)
        const result = await userStore.initUser(token, email, name)

        if (result.needsSignup) {
            // Check if user pre-validated a code on the login page (social signup flow)
            const pendingRaw = localStorage.getItem('cs_pending_signup')
            if (pendingRaw) {
                try {
                    const pending = JSON.parse(pendingRaw)
                    localStorage.removeItem('cs_pending_signup')
                    if (pending.path === 'supplier-claim' && pending.code) {
                        router.push({ path: '/signup', query: { claim: pending.code } })
                    } else if (pending.path === 'advisor-invite' && pending.code) {
                        router.push({ path: '/signup', query: { invite: pending.code } })
                    } else {
                        router.push({ path: '/signup/complete-oauth' })
                    }
                } catch {
                    localStorage.removeItem('cs_pending_signup')
                    router.push({ path: '/signup/complete-oauth' })
                }
            } else {
                // Authenticated via Auth0 (OAuth or password) but no DB user.
                // Route to the role-pick form so they can finish setting up.
                router.push({ path: '/signup/complete-oauth' })
            }
            return
        }

        trackEvent('login', { method: 'social' })
        router.push(userStore.getOnboardingRoute())
    } catch (e: unknown) {
        console.error('Auth callback error:', e)
        const msg = e instanceof Error ? e.message : 'Failed to complete login'
        if (msg.toLowerCase().includes('network')) {
            error.value = 'Unable to connect to the server. Please check your connection and try again.'
        } else if (msg.includes('500') || msg.includes('Internal Server')) {
            error.value = 'The server encountered an error. Please try again in a moment.'
        } else {
            error.value = msg
        }
    }
})
</script>

<template>
    <div class="min-h-screen bg-cc-deep flex items-center justify-center">
        <div class="bg-cc-elevated rounded-2xl shadow-cc-card p-8 max-w-md w-full text-center border border-cc-border">
            <div v-if="!error">
                <div class="animate-spin w-12 h-12 border-4 border-cc-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                <p class="text-cc-text-secondary">{{ status }}</p>
            </div>
            <div v-else>
                <div class="w-12 h-12 bg-cc-danger/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span class="text-cc-danger text-2xl">!</span>
                </div>
                <p class="text-cc-danger font-medium">{{ error }}</p>
                <div class="mt-4 flex gap-3 justify-center">
                    <button
                        @click="retry"
                        class="px-4 py-2 bg-cc-primary text-cc-btn-text rounded-cc hover:opacity-90"
                    >
                        Try Again
                    </button>
                    <button
                        @click="router.push('/login')"
                        class="px-4 py-2 bg-cc-surface text-cc-text-secondary rounded-cc hover:bg-cc-deep"
                    >
                        Back to Login
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
